import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../components/MainLayout';
import Modal from '../../components/Modal';
import Pagination from '../../components/common/Pagination';
import { fetchUsersForManagement, deleteUser } from '../../services/api';
import { hasPermission } from '../../services/authService';
import './UserListPage.css';

function UserListPage() {
    const navigate = useNavigate();
    const [selectedRole, setSelectedRole] = useState('전체');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    // 페이지네이션 상태
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);

    // 권한 체크
    const canCreate = hasPermission('user.create');
    const canUpdate = hasPermission('user.update');
    const canDelete = hasPermission('user.delete');

    // 상태 매핑 함수
    const mapStatus = (apiStatus) => {
        const statusMap = {
            'ACTIVE': '활성',
            'INACTIVE': '비활성',
            'LOCKED': '잠김'
        };
        return statusMap[apiStatus] || '비활성';
    };

    // 날짜 포맷 함수 (2021. 12. 12. 형식)
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // 날짜시간 포맷 함수 (2021. 12. 12. 14:30 형식)
    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '-';
        const date = new Date(dateTimeString);
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }) + ' ' + date.toLocaleTimeString('ko-KR', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });
    };

    // 사용자 데이터 변환 함수
    const transformUserData = (user) => {
        // 권한 그룹 정보 처리
        let roleGroup = null;
        if (user.roleGroup) {
            // 백엔드에서 받은 권한 그룹 데이터 사용
            roleGroup = {
                id: user.roleGroup.id,
                name: user.roleGroup.name,
                code: user.roleGroup.code,
                description: user.roleGroup.description,
                permissions: user.roleGroup.permissions || []
            };
        } else {
            // roleGroup이 없는 경우 기본값 (하위 호환성)
            let roleName, roleCode;
            if (user.isSuperAdmin) {
                roleName = '슈퍼 관리자';
                roleCode = 'SUPER_ADMIN';
            } else if (user.isCompanyAdmin) {
                roleName = '시스템 관리자';
                roleCode = 'ADMIN';
            } else {
                roleName = '일반 사용자';
                roleCode = 'NORMAL';
            }
            roleGroup = {
                id: user.isSuperAdmin ? 0 : (user.isCompanyAdmin ? 1 : 2),
                name: roleName,
                code: roleCode,
                description: '',
                permissions: []
            };
        }

        return {
            id: user.id,
            name: user.name,
            email: user.email,
            phone: user.phone || '-',
            roleGroup: roleGroup,
            department: user.department || '-',
            position: user.position || '-',
            status: mapStatus(user.status),
            lastLogin: user.lastLogin ? formatDateTime(user.lastLogin) : '-',
            joinDate: user.createdAt ? formatDate(user.createdAt) : '-',
            isCompanyAdmin: user.isCompanyAdmin,
            isSuperAdmin: user.isSuperAdmin,
            companyName: user.companyName
        };
    };

    // API에서 회원 목록 가져오기
    useEffect(() => {
        const loadUsers = async () => {
            try {
                setLoading(true);
                const data = await fetchUsersForManagement();
                

                // API 데이터를 화면에 맞게 변환
                const usersData = Array.isArray(data) ? data : [];
                const transformedUsers = usersData.map(transformUserData);

                setAllUsers(transformedUsers);
                setUsers(transformedUsers);
            } catch (err) {
                console.error('회원 목록 조회 실패:', err);
                setAllUsers([]);
                setUsers([]);
            } finally {
                setLoading(false);
            }
        };

        loadUsers();
    }, []);

    // 필터링 로직
    useEffect(() => {
        let filtered = [...allUsers];

        // 역할 필터링
        if (selectedRole !== '전체') {
            filtered = filtered.filter(user => user.roleGroup.name === selectedRole);
        }

        // 검색어 필터링
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(user =>
                user.name.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
            );
        }

        setUsers(filtered);
        setPage(0); // 필터 변경시 첫 페이지로
    }, [selectedRole, searchQuery, allUsers]);

    // 페이지네이션 적용
    const paginatedUsers = useMemo(() => {
        const startIndex = page * size;
        return users.slice(startIndex, startIndex + size);
    }, [users, page, size]);

    const totalElements = users.length;
    const totalPages = Math.ceil(totalElements / size);

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleSizeChange = (newSize) => {
        setSize(newSize);
        setPage(0);
    };

    const mockUsers = [
        {
            id: 1,
            name: '김관리',
            email: 'admin@company.net',
            roleGroup: {
                id: 1,
                name: '시스템 관리자',
                code: 'ADMIN',
                permissions: {
                    dashboard: true,
                    monitoring: true,
                    factory: true,
                    equipment: true,
                    user: true,
                    system: true
                }
            },
            department: 'IT팀',
            status: '활성',
            lastLogin: '2024-02-01 14:30',
            joinDate: '2023-01-15'
        },
        {
            id: 2,
            name: '이매니저',
            email: 'manager@company.net',
            roleGroup: {
                id: 2,
                name: '공장 관리자',
                code: 'FACTORY_MANAGER',
                permissions: {
                    dashboard: true,
                    monitoring: true,
                    factory: true,
                    equipment: true,
                    user: false,
                    system: false
                }
            },
            department: '생산팀',
            status: '활성',
            lastLogin: '2024-02-01 10:20',
            joinDate: '2023-03-20'
        },
        {
            id: 3,
            name: '박모니터',
            email: 'monitor@company.net',
            roleGroup: {
                id: 3,
                name: '모니터링 담당자',
                code: 'MONITOR',
                permissions: {
                    dashboard: true,
                    monitoring: true,
                    factory: false,
                    equipment: false,
                    user: false,
                    system: false
                }
            },
            department: '관리팀',
            status: '비활성',
            lastLogin: '2024-01-28 16:45',
            joinDate: '2023-06-10'
        }
    ];

    const getInitials = (name) => {
        return name.charAt(0);
    };

    const getStatusClass = (status) => {
        return status === '활성' ? 'status-active' : 'status-inactive';
    };

    const permissionLabels = {
        dashboard: '대시보드',
        monitoring: '모니터링',
        factory: '공장관리',
        equipment: '설비관리',
        user: '회원관리',
        role: '권한관리',
        system: '시스템관리'
    };

    const getActivePermissions = (permissions) => {
        // 백엔드에서 받은 권한 배열인 경우
        if (Array.isArray(permissions)) {
            return permissions.map(perm => perm.name);
        }

        // 기존 객체 형식 (하위 호환성)
        if (typeof permissions === 'object' && permissions !== null) {
            return Object.entries(permissions)
                .filter(([_, value]) => value)
                .map(([key]) => permissionLabels[key] || key);
        }

        return [];
    };

    const handleEditClick = (userId) => {
        navigate(`/user/${userId}?edit=true`);
    };

    const handleDeleteClick = (user) => {
        setSelectedUser(user);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        try {
            
            await deleteUser(selectedUser.id);

            alert(`"${selectedUser.name}" 회원이 삭제되었습니다.`);
            setIsDeleteModalOpen(false);

            // 목록 새로고침
            const data = await fetchUsersForManagement();
            const usersData = Array.isArray(data) ? data : [];
            const transformedUsers = usersData.map(transformUserData);

            setAllUsers(transformedUsers);
            setUsers(transformedUsers);
        } catch (error) {
            console.error('회원 삭제 실패:', error);
            alert('회원 삭제에 실패했습니다.');
        }
    };

    return (
        <MainLayout>
            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: 'white' }}>
                    데이터를 불러오는 중...
                </div>
            ) : (
            <div className="user-list-container">
                {/* Header Section */}
                <div className="user-list-header">
                    <div className="user-list-header-left">
                        <div className="user-list-title">회원 관리</div>
                        <p className="user-list-subtitle">시스템 사용자 계정 및 권한 관리</p>
                    </div>
                    <div className="user-list-header-right">
                        {/* <div className="update-label">업데이트</div>
                        <div className="update-time">실시간</div> */}
                    </div>
                </div>

                <div className="user-list-content">
                    {/* Filter and Search Section */}
                    <div className="user-list-controls">
                        <div className="control-left">
                            <label className="control-label">역할</label>
                            <div className="dropdown-container">
                                <select
                                    className="role-dropdown"
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                >
                                    <option value="전체">전체</option>
                                    <option value="시스템 관리자">시스템 관리자</option>
                                    <option value="일반 사용자">일반 사용자</option>
                                </select>
                                <svg className="dropdown-icon" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                    <path d="M2 4L6 8L10 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                        </div>

                        <div className="control-right">
                            <div className="search-container">
                                <input
                                    type="text"
                                    className="search-input"
                                    placeholder="이름 또는 이메일 검색"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                                <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M7.33333 12.6667C10.2789 12.6667 12.6667 10.2789 12.6667 7.33333C12.6667 4.38781 10.2789 2 7.33333 2C4.38781 2 2 4.38781 2 7.33333C2 10.2789 4.38781 12.6667 7.33333 12.6667Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    <path d="M14 14L11.1 11.1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            {canCreate && (
                                <button className="add-user-btn" onClick={() => navigate('/user/register')}>
                                    <svg className="plus-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                        <path d="M8 3V13M3 8H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    사용자 추가
                                </button>
                            )}
                        </div>
                    </div>

                    {/* User Table */}
                    <div className="user-table-container">
                        <table className="user-table">
                            <thead>
                                <tr>
                                    <th>사용자</th>
                                    <th>직위</th>
                                    <th>부서</th>
                                    <th>상태</th>
                                    <th>마지막 로그인</th>
                                    <th>가입일</th>
                                    <th>권한 그룹</th>
                                    <th>권한 설정</th>
                                    <th>작업</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedUsers.map((user) => (
                                    <tr key={user.id} onClick={() => navigate(`/user/${user.id}`)} style={{ cursor: 'pointer' }}>
                                        <td className="user-cell">
                                            <div className="user-info">
                                                <div className="user-avatar">{getInitials(user.name)}</div>
                                                <div className="user-details">
                                                    <div className="user-name">{user.name}</div>
                                                    <div className="user-email">{user.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td>{user.position}</td>
                                        <td>{user.department}</td>
                                        <td>
                                            <span className={`status-badge ${getStatusClass(user.status)}`}>
                                                {user.status}
                                            </span>
                                        </td>
                                        <td>{user.lastLogin}</td>
                                        <td>{user.joinDate}</td>
                                        <td>
                                            <div className="role-group-cell">
                                                <div className="role-group-name">{user.roleGroup.name}</div>
                                                <div className="role-group-code">{user.roleGroup.code}</div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className="permissions-cell">
                                                {getActivePermissions(user.roleGroup.permissions).slice(0, 2).map((perm, idx) => (
                                                    <span key={idx} className="permission-tag">{perm}</span>
                                                ))}
                                                {getActivePermissions(user.roleGroup.permissions).length > 2 && (
                                                    <span className="permission-tag more-tag">
                                                        +{getActivePermissions(user.roleGroup.permissions).length - 2}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                            <div className="action-buttons">
                                                {canUpdate && (
                                                    <button className="action-btn edit-btn" onClick={() => handleEditClick(user.id)}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M11.3333 2C11.5084 1.82493 11.7163 1.68605 11.9457 1.59135C12.1752 1.49665 12.4218 1.44803 12.6707 1.44803C12.9196 1.44803 13.1662 1.49665 13.3956 1.59135C13.6251 1.68605 13.833 1.82493 14.008 2C14.183 2.17507 14.3219 2.38299 14.4166 2.61245C14.5113 2.84191 14.5599 3.08852 14.5599 3.33744C14.5599 3.58636 14.5113 3.83297 14.4166 4.06243C14.3219 4.29189 14.183 4.49981 14.008 4.67488L5.33333 13.3495L1.33333 14.683L2.66667 10.6829L11.3333 2Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                                {canDelete && (
                                                    <button className="action-btn delete-btn" onClick={() => handleDeleteClick(user)}>
                                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                            <path d="M2 4H3.33333H14M5.33333 4V2.66667C5.33333 2.31305 5.47381 1.97391 5.72386 1.72386C5.97391 1.47381 6.31305 1.33333 6.66667 1.33333H9.33333C9.68696 1.33333 10.0261 1.47381 10.2761 1.72386C10.5262 1.97391 10.6667 2.31305 10.6667 2.66667V4M6.66667 7.33333V11.3333M9.33333 7.33333V11.3333M3.33333 4V13.3333C3.33333 13.687 3.47381 14.0261 3.72386 14.2761C3.97391 14.5262 4.31305 14.6667 4.66667 14.6667H11.3333C11.687 14.6667 12.0261 14.5262 12.2761 14.2761C12.5262 14.0261 12.6667 13.687 12.6667 13.3333V4H3.33333Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {totalElements > 0 && (
                            <Pagination
                                page={page}
                                totalPages={totalPages}
                                totalElements={totalElements}
                                size={size}
                                onPageChange={handlePageChange}
                                onSizeChange={handleSizeChange}
                            />
                        )}
                    </div>
                </div>
            </div>
            )}

            <Modal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDeleteConfirm}
                title="회원 삭제"
                message={selectedUser ? `"${selectedUser.name}" 회원을 삭제하시겠습니까?\n이 작업은 되돌릴 수 없습니다.` : ''}
                confirmText="삭제"
                cancelText="취소"
                type="danger"
            />
        </MainLayout>
    );
}

export default UserListPage;